'use client';

import Link from 'next/link';
import { EnvironmentOutlined, ClockCircleOutlined, PhoneOutlined } from '@ant-design/icons';
import { useTranslation } from '@root/src/i18n/client';

function ContactItem({
  icon,
  title,
  desc,
}: {
  icon: React.ReactNode;
  title: string;
  desc: string;
}) {
  return (
    <div className="flex min-h-[132px] flex-col gap-3 rounded-[22px] border border-[#e9dcc4] bg-white/85 p-5 shadow-[0_12px_30px_rgba(82,64,34,0.08)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_18px_34px_rgba(82,64,34,0.14)] sm:p-6">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#f4ead8] text-[#7b5f24]">
        {icon}
      </div>
      <div className="space-y-1">
        <p className="text-sm font-semibold uppercase tracking-[0.16em] text-[#a0813a]">
          {title}
        </p>
        <p className="text-sm leading-6 text-[#4c4a44]">{desc}</p>
      </div>
    </div>
  );
}

export default function TrustBadges() {
  const { t } = useTranslation('common');

  return (
    <section className="w-full border-y border-[#ede5d4] bg-[#fbf7ef] py-6 sm:py-8">
      <div className="mx-auto max-w-[1480px] px-3 sm:px-6 lg:px-8">
        <div className="grid gap-5 lg:grid-cols-[1.15fr_1fr] lg:items-center">
          <div className="space-y-4 rounded-[28px] border border-[#eadfc8] bg-[linear-gradient(180deg,rgba(255,255,255,0.88),rgba(246,236,219,0.92))] p-5 shadow-[0_14px_32px_rgba(82,64,34,0.08)] sm:p-7">
            <div className="inline-flex rounded-full bg-[#c6a869]/12 px-4 py-1.5">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#a0813a]">
                {t('lytra.contactWidgetTitle')}
              </span>
            </div>
            <h2
              className="text-3xl leading-tight text-[#2f3c30] sm:text-4xl"
              style={{
                fontFamily: 'Georgia, Times New Roman, serif',
                fontWeight: 400,
              }}
            >
              {t('Contact Us')}
            </h2>
            <p className="max-w-2xl text-sm leading-7 text-[#6c6558] sm:text-base">
              {t('lytra.contactHeroDesc')}
            </p>

            <div className="flex flex-wrap gap-3 pt-2">
              <Link
                href="/contact"
                className="inline-flex h-11 items-center justify-center rounded-full bg-[#2f3c30] px-5 text-xs font-semibold uppercase tracking-[0.16em] text-white transition hover:bg-[#223025]"
              >
                {t('Contact Us')}
              </Link>
              <a
                href="tel:0931338566"
                className="inline-flex h-11 items-center justify-center rounded-full border border-[#d8c7a4] bg-white px-5 text-xs font-semibold uppercase tracking-[0.16em] text-[#3c4b35] transition hover:border-[#a0813a] hover:text-[#223A27]"
              >
                {t('lytra.callLabel')} 0931 338 566
              </a>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1 xl:grid-cols-3">
            <ContactItem
              icon={<PhoneOutlined />}
              title={t('lytra.contactHotlineTitle')}
              desc="0931 338 566"
            />
            <ContactItem
              icon={<EnvironmentOutlined />}
              title={t('lytra.contactStoreTitle')}
              desc={t('AddressContactss')}
            />
            <ContactItem
              icon={<ClockCircleOutlined />}
              title={t('lytra.contactSupportTimeTitle')}
              desc={t('lytra.contactSupportTimeValue')}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
